import { c as _c } from "react/compiler-runtime";
import { ReactNode } from 'react';
import { styled } from '@classify/react-core-components/styles';
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';
import Typography from '@classify/react-core-components/Typography';
import CircularLearningProgress from '@classify/react-product-components/CircularLearningProgress';
import ProductMetadata from '@classify/react-product-components/ProductMetadata';
import ProductMetadataDuration from '@classify/react-product-components/ProductMetadataDuration';
import durationDefaultTranslations, { TranslationsProp as DurationTranslationsProp } from '@classify/react-product-components/ProductMetadataDuration/defaultTranslations';
import Box from '@classify/react-core-components/Box';
import BaseCourseItem, { BaseCourseItemTranslations, Props as BaseCourseItemProps } from '../BaseCourseItem';
interface CourseItemTranslations extends BaseCourseItemTranslations, DurationTranslationsProp {
  levelAriaLabel?: string;
  durationAriaLabel?: string;
  completionLabel?: string;
  completedLabel?: string;
  followedAtLabel?: string;
}
export interface Props extends BaseCourseItemProps {
  level?: string;
  duration?: string;
  description?: string;
  completion?: number | null;
  metadata?: ReactNode;
  translations?: CourseItemTranslations;
}
const ContentContainer = styled('div')(({
  theme: {
    spacing
  }
}) => ({
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  marginTop: spacing(1)
}));
const InfoContainer = styled('div')(({
  theme: {
    spacing
  }
}) => ({
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: spacing(2),
  marginBottom: spacing(1)
}));
const Description = styled(Typography)(({
  theme: {
    palette,
    breakpoints
  }
}) => ({
  color: palette.text.onMain,
  [breakpoints.up('sm')]: {
    display: 'flex'
  }
}));
const Completion = styled(Box)(({
  theme: {
    spacing
  }
}) => ({
  display: 'flex',
  alignItems: 'center',
  gap: spacing(1),
  marginTop: 'auto'
}));
const defaultTranslations: CourseItemTranslations = {
  completionLabel: 'Completion',
  completedLabel: 'Course complete!',
  removeAriaLabel: 'Remove',
  levelAriaLabel: 'Level',
  durationAriaLabel: 'Duration',
  ...durationDefaultTranslations
};
const CourseItem = t0 => {
  const $ = _c(44);
  let category;
  let description;
  let duration;
  let illustration;
  let level;
  let metadata;
  let onRemove;
  let otherProps;
  let t1;
  let t2;
  let title;
  let translations;
  if ($[0] !== t0) {
    ({
      title,
      category,
      level,
      duration,
      description,
      illustration,
      completion: t1,
      metadata,
      translations,
      isDraggable: t2,
      onRemove,
      ...otherProps
    } = t0);
    $[0] = t0;
    $[1] = category;
    $[2] = description;
    $[3] = duration;
    $[4] = illustration;
    $[5] = level;
    $[6] = metadata;
    $[7] = onRemove;
    $[8] = otherProps;
    $[9] = t1;
    $[10] = t2;
    $[11] = title;
    $[12] = translations;
  } else {
    category = $[1];
    description = $[2];
    duration = $[3];
    illustration = $[4];
    level = $[5];
    metadata = $[6];
    onRemove = $[7];
    otherProps = $[8];
    t1 = $[9];
    t2 = $[10];
    title = $[11];
    translations = $[12];
  }
  const completion = t1 === undefined ? null : t1;
  const isDraggable = t2 === undefined ? false : t2;
  let t3;
  if ($[13] !== translations) {
    t3 = {
      ...defaultTranslations,
      ...translations
    };
    $[13] = translations;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  const mergedTranslations = t3;
  let t4;
  if ($[15] !== mergedTranslations.removeAriaLabel) {
    t4 = {
      removeAriaLabel: mergedTranslations.removeAriaLabel
    };
    $[15] = mergedTranslations.removeAriaLabel;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  let t5;
  if ($[17] !== duration || $[18] !== level || $[19] !== mergedTranslations || $[20] !== metadata) {
    t5 = (level || duration || metadata) && <InfoContainer>{level && <ProductMetadata icon={SignalCellularAltIcon} accessibilityLabel={mergedTranslations.levelAriaLabel}>{level}</ProductMetadata>}{duration && <ProductMetadataDuration duration={duration} accessibilityLabel={mergedTranslations.durationAriaLabel as string} translations={mergedTranslations} />}{metadata}</InfoContainer>;
    $[17] = duration;
    $[18] = level;
    $[19] = mergedTranslations;
    $[20] = metadata;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  let t6;
  if ($[22] !== description) {
    t6 = description && <Description sx={{
      display: {
        xs: "none",
        sm: "block"
      },
      maxWidth: "768px"
    }} variant="bodySmall">{description}</Description>;
    $[22] = description;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  let t7;
  if ($[24] !== completion || $[25] !== mergedTranslations.completedLabel || $[26] !== mergedTranslations.completionLabel) {
    t7 = completion !== null && <Completion><CircularLearningProgress value={completion} maxValue={100} />{completion === 100 ? <Typography variant="subheadingSmall" color="progress.learningAsset.passed">{mergedTranslations.completedLabel}</Typography> : <Typography variant="bodySmall">{mergedTranslations.completionLabel}</Typography>}</Completion>;
    $[24] = completion;
    $[25] = mergedTranslations.completedLabel;
    $[26] = mergedTranslations.completionLabel;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  let t8;
  if ($[28] !== mergedTranslations.followedAtLabel) {
    t8 = mergedTranslations.followedAtLabel && <Typography variant="caption" sx={{
      mt: 1
    }}>{mergedTranslations.followedAtLabel}</Typography>;
    $[28] = mergedTranslations.followedAtLabel;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  let t9;
  if ($[30] !== t5 || $[31] !== t6 || $[32] !== t7 || $[33] !== t8) {
    t9 = <ContentContainer>{t5}{t6}{t7}{t8}</ContentContainer>;
    $[30] = t5;
    $[31] = t6;
    $[32] = t7;
    $[33] = t8;
    $[34] = t9;
  } else {
    t9 = $[34];
  }
  let t10;
  if ($[35] !== category || $[36] !== illustration || $[37] !== isDraggable || $[38] !== onRemove || $[39] !== otherProps || $[40] !== t4 || $[41] !== t9 || $[42] !== title) {
    t10 = <BaseCourseItem category={category} title={title} illustration={illustration} translations={t4} isDraggable={isDraggable} onRemove={onRemove} {...otherProps}>{t9}</BaseCourseItem>;
    $[35] = category;
    $[36] = illustration;
    $[37] = isDraggable;
    $[38] = onRemove;
    $[39] = otherProps;
    $[40] = t4;
    $[41] = t9;
    $[42] = title;
    $[43] = t10;
  } else {
    t10 = $[43];
  }
  return t10;
};
export default CourseItem;